import React, { useEffect, useMemo, useState } from "react";
import SiteHeader from "../components/SiteHeader";
import Reveal from "../components/Reveal";
import { IconArrowRight, IconChat, IconClose, IconLink, IconSearch } from "../components/Icons";
import { buildFaqs, FaqCategory, FaqItem } from "../data/faqs";
import { SHOW_MERCHANT_PLANS, SIGNUP_URL, WEBAPP_URL } from "../siteConfig";
import { useDocumentMeta } from "../useDocumentMeta";
import "../styles/faq.css";

const normalize = (s: string) => s.toLowerCase().trim();

const FaqPage: React.FC = () => {
  const faqs: FaqItem[] = useMemo(() => buildFaqs(WEBAPP_URL, SIGNUP_URL), []);

  const categories: FaqCategory[] = useMemo(() => {
    const uniq = Array.from(new Set(faqs.map((f) => f.category)));
    return uniq as FaqCategory[];
  }, [faqs]);

  const [query, setQuery] = useState<string>("");
  const [activeCategory, setActiveCategory] = useState<FaqCategory | "All">("All");
  const [openIds, setOpenIds] = useState<Record<string, boolean>>({});

  const filteredFaqs = useMemo(() => {
    const q = normalize(query);

    return faqs.filter((f) => {
      if (activeCategory !== "All" && f.category !== activeCategory) return false;
      if (!q) return true;

      const hay = [f.question, f.answerText, f.category, ...(f.keywords || [])]
        .join(" ")
        .toLowerCase();

      return hay.includes(q);
    });
  }, [faqs, query, activeCategory]);

  /* While searching, every match is expanded so hits are visible at a glance. */
  useEffect(() => {
    const q = normalize(query);
    if (!q) {
      setOpenIds({});
      return;
    }

    const next: Record<string, boolean> = {};
    for (const f of filteredFaqs) next[f.id] = true;
    setOpenIds(next);
  }, [query, filteredFaqs]);

  /* A deep link such as /faq#merchant-join should open the item it points at,
     not just scroll to a collapsed row. */
  useEffect(() => {
    const openFromHash = () => {
      const id = window.location.hash.slice(1);
      if (!id) return;
      const el = document.getElementById(id);
      if (el instanceof HTMLDetailsElement) {
        el.open = true;
        el.scrollIntoView({ block: "start" });
      }
    };

    openFromHash();
    window.addEventListener("hashchange", openFromHash);
    return () => window.removeEventListener("hashchange", openFromHash);
  }, []);

  useDocumentMeta({
    title: "FAQ | PABS",
    description:
      `Frequently asked questions about PABS — Foods, Pabili (including pharmacy), Pasugo errands, Angkas rides, payments, ${SHOW_MERCHANT_PLANS ? "merchant plans, " : ""}and privacy.`,
    canonicalPath: "/faq",
    ogImage: "https://pabs.online/logo512.png",
    jsonLdId: "pabs-faq-jsonld",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: faqs.map((f) => ({
        "@type": "Question",
        name: f.question,
        acceptedAnswer: {
          "@type": "Answer",
          text: f.answerText,
        },
      })),
    },
  });

  const grouped = useMemo(() => {
    const map = new Map<FaqCategory, FaqItem[]>();
    for (const c of categories) map.set(c, []);
    for (const f of filteredFaqs) map.get(f.category)?.push(f);
    return map;
  }, [categories, filteredFaqs]);

  const isSearching = Boolean(normalize(query));
  const resultCount = filteredFaqs.length;

  const resetFilters = () => {
    setQuery("");
    setActiveCategory("All");
  };

  return (
    <>
      <SiteHeader current="/faq" />

      <main id="main" className="fq">
        {/* ================= Hero + search ================= */}
        <section className="fq__hero">
          <div className="fq__bg" aria-hidden="true" />
          <div className="u-container">
            <Reveal className="fq__head">
              <span className="t-eyebrow">Help center</span>
              <h1 className="t-h1 fq__title">
                Frequently asked <span className="t-grad">questions.</span>
              </h1>
              <p className="t-lead fq__lead">
                Quick answers about Foods, Pabili and pharmacy
                deliveries, Pasugo errands, Angkas rides, and payments
                {SHOW_MERCHANT_PLANS ? ", plus merchant plans" : ""}.
              </p>

              <div className="fq__search" role="search">
                <label className="u-sr" htmlFor="faqSearch">
                  Search FAQs
                </label>
                <IconSearch size={19} className="fq__search-icon" />
                <input
                  id="faqSearch"
                  type="search"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder={`Search: pasugo, food, grocery, ${SHOW_MERCHANT_PLANS ? "payment, merchant" : "ride, payment"}…`}
                  autoComplete="off"
                />
                {query ? (
                  <button
                    className="fq__search-clear"
                    type="button"
                    onClick={() => setQuery("")}
                    aria-label="Clear search"
                  >
                    <IconClose size={17} />
                  </button>
                ) : null}
              </div>

              {/* Filter chips scroll horizontally on mobile rather than wrapping
                 into a tall block that pushes the answers off screen. */}
              <div className="fq__chips" role="group" aria-label="Filter by category">
                <button
                  type="button"
                  className={`fq__chip${activeCategory === "All" ? " is-active" : ""}`}
                  aria-pressed={activeCategory === "All"}
                  onClick={() => setActiveCategory("All")}
                >
                  All
                </button>
                {categories.map((c) => (
                  <button
                    key={c}
                    type="button"
                    className={`fq__chip${activeCategory === c ? " is-active" : ""}`}
                    aria-pressed={activeCategory === c}
                    onClick={() => setActiveCategory(c)}
                  >
                    {c}
                  </button>
                ))}
              </div>

              <p className="fq__count" role="status" aria-live="polite">
                {isSearching || activeCategory !== "All"
                  ? `${resultCount} ${resultCount === 1 ? "question" : "questions"} matching`
                  : `${faqs.length} questions across ${categories.length} topics`}
              </p>
            </Reveal>
          </div>
        </section>

        {/* ================= Content ================= */}
        <section className="fq__content">
          <div className="u-container fq__layout">
            <aside className="fq__side" aria-label="FAQ categories">
              <div className="fq__side-card">
                <h2 className="fq__side-title">Browse</h2>
                <div className="fq__side-links">
                  <button
                    type="button"
                    className={`fq__side-link${
                      activeCategory === "All" ? " is-active" : ""
                    }`}
                    onClick={() => setActiveCategory("All")}
                  >
                    All questions
                    <span className="fq__side-n">{faqs.length}</span>
                  </button>
                  {categories.map((c) => (
                    <button
                      key={c}
                      type="button"
                      className={`fq__side-link${
                        activeCategory === c ? " is-active" : ""
                      }`}
                      onClick={() => setActiveCategory(c)}
                    >
                      {c}
                      <span className="fq__side-n">
                        {faqs.filter((f) => f.category === c).length}
                      </span>
                    </button>
                  ))}
                </div>

                <div className="fq__side-note">
                  Can't find what you need? <a href="/contact">Contact us</a>.
                </div>
              </div>
            </aside>

            <div className="fq__list">
              {resultCount === 0 ? (
                <div className="fq__empty">
                  <span className="fq__empty-icon">
                    <IconSearch size={26} />
                  </span>
                  <h2 className="t-h3">No results</h2>
                  <p className="t-sm">
                    Try a different search (e.g. <em>food</em>, <em>delivery</em>,{" "}
                    <em>payment</em>) or clear the filters.
                  </p>
                  <button type="button" className="btn btn--secondary" onClick={resetFilters}>
                    Reset filters
                  </button>
                </div>
              ) : null}

              {Array.from(grouped.entries()).map(([category, items]) => {
                if (!items.length) return null;
                return (
                  <section key={category} className="fq__group">
                    <h2 className="fq__group-title">
                      {category}
                      <span className="fq__group-n">{items.length}</span>
                    </h2>

                    <div className="fq__items">
                      {items.map((f) => (
                        <details
                          key={f.id}
                          id={f.id}
                          className="fq__item"
                          open={isSearching ? Boolean(openIds[f.id]) : undefined}
                          onToggle={(e) => {
                            if (!isSearching) return;
                            const el = e.currentTarget as HTMLDetailsElement;
                            setOpenIds((prev) => ({ ...prev, [f.id]: el.open }));
                          }}
                        >
                          <summary className="fq__q">
                            <span>{f.question}</span>
                            <span className="fq__marker" aria-hidden="true">
                              <i />
                              <i />
                            </span>
                          </summary>
                          <div className="fq__a">
                            {f.answer}
                            <a className="fq__permalink" href={`#${f.id}`}>
                              <IconLink size={14} />
                              Link to this question
                            </a>
                          </div>
                        </details>
                      ))}
                    </div>
                  </section>
                );
              })}

              <section className="fq__cta" aria-label="Still have questions">
                <div className="fq__cta-copy">
                  <h2 className="t-h3">Still have questions?</h2>
                  <p>
                    If you need help with orders, deliveries, or your account,
                    send us a message.
                  </p>
                  <div className="btn-row fq__cta-actions">
                    <a className="btn btn--light" href="/contact">
                      <IconChat size={17} />
                      Contact us
                    </a>
                    <a className="btn btn--outline-light" href={SIGNUP_URL}>
                      Create account
                      <IconArrowRight size={17} className="btn__icon btn__icon--arrow" />
                    </a>
                  </div>
                </div>
                <ul className="fq__cta-meta">
                  <li>Typical response: 1–2 business days</li>
                  <li>Sign up with Google, Apple, or your phone</li>
                </ul>
              </section>
            </div>
          </div>
        </section>
      </main>
    </>
  );
};

export default FaqPage;
